import { useState } from "react";
import { Trash2, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import ImageListEditor from "@/components/admin/ImageListEditor";
import { OPTION_ORDER, optionLabel } from "@/components/kingsley/OptionGroup";
import { field } from "@/pages/admin/AdminLogin";

const toRows = (o = {}) => Object.entries(o).map(([key, vals]) => ({ key, values: vals.join(", ") }));
const fromRows = (rows) =>
  Object.fromEntries(rows.map((r) => [r.key, r.values.split(",").map((v) => v.trim()).filter(Boolean)]).filter(([, v]) => v.length));

function L({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="overline mb-2 block text-[0.6rem] text-muted">{label}</span>
      {children}
    </label>
  );
}

function OptionsEditor({ rows, setRows }) {
  const free = OPTION_ORDER.filter((k) => !rows.some((r) => r.key === k));
  const set = (i, patch) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <div data-testid="product-options-editor" className="space-y-2">
      {rows.map((r, i) => (
        <div key={r.key} className="flex items-center gap-2">
          <span className="w-24 shrink-0 font-body text-sm text-ink">{optionLabel(r.key)}</span>
          <input data-testid={`product-option-${r.key}`} value={r.values} onChange={(e) => set(i, { values: e.target.value })} placeholder="Comma separated, e.g. S, M, L" className={field} />
          <button type="button" onClick={() => setRows(rows.filter((_, j) => j !== i))} className="p-2 text-stone hover:text-red-700" aria-label="Remove option"><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      {free.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-1">
          {free.map((k) => (
            <button key={k} type="button" data-testid={`product-add-option-${k}`} onClick={() => setRows([...rows, { key: k, values: "" }])}
              className="inline-flex items-center gap-1 border border-ink/20 px-2.5 py-1 font-body text-xs text-stone hover:border-ink">
              <Plus className="h-3 w-3" /> {optionLabel(k)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ProductForm({ product, cats, onSave, onClose }) {
  const [f, setF] = useState({
    name: "", category: cats[0]?.id || "", kind: "made-to-order", price: "", stock: "", lead_time: "", blurb: "",
    images: [], published: true, sort: 0, ...product,
    ...(product.id ? { price: product.price ?? "", stock: product.stock ?? "" } : {}),
  });
  const [rows, setRows] = useState(toRows(product.options));
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    await onSave({ ...f, price: f.price === "" ? null : Number(f.price), stock: f.stock === "" ? null : parseInt(f.stock, 10), options: fromRows(rows) });
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="product-form-dialog" className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-none bg-paper" data-lenis-prevent>
        <DialogHeader><DialogTitle className="font-display text-3xl font-extralight">{product.id ? "Edit item" : "New item"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <L label="Name"><input data-testid="product-name" required value={f.name} onChange={set("name")} className={field} /></L>
          <div className="grid gap-4 sm:grid-cols-2">
            <L label="Room">
              <select data-testid="product-category" value={f.category} onChange={set("category")} className={field}>
                {cats.filter((c) => c.id !== "ready").map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
              </select>
            </L>
            <L label="Type">
              <select data-testid="product-kind" value={f.kind} onChange={set("kind")} className={field}>
                <option value="made-to-order">Made to order</option>
                <option value="ready-to-wear">Ready to wear / one-off</option>
              </select>
            </L>
            <L label="Price (USD) — empty = on request"><input data-testid="product-price" type="number" min="0" step="0.01" value={f.price} onChange={set("price")} className={field} /></L>
            <L label="Stock — empty = unlimited"><input data-testid="product-stock" type="number" min="0" step="1" value={f.stock} onChange={set("stock")} className={field} /></L>
          </div>
          <L label="Lead time / availability line"><input data-testid="product-lead-time" value={f.lead_time} onChange={set("lead_time")} placeholder="Made to order · ships in 3–4 weeks" className={field} /></L>
          <L label="Description"><textarea data-testid="product-blurb" rows={4} value={f.blurb} onChange={set("blurb")} className={field} /></L>
          <div><span className="overline mb-2 block text-[0.6rem] text-muted">Photos</span><ImageListEditor images={f.images} onChange={(images) => setF({ ...f, images })} testid="product-images" /></div>
          <div><span className="overline mb-2 block text-[0.6rem] text-muted">Choices buyers pick from</span><OptionsEditor rows={rows} setRows={setRows} /></div>
          <div className="flex items-center gap-3">
            <Switch data-testid="product-published" checked={f.published} onCheckedChange={(v) => setF({ ...f, published: v })} />
            <span className="font-body text-sm text-ink">Live in the store</span>
          </div>
          <div className="flex items-center gap-3">
            <Switch data-testid="product-one-of-a-kind" checked={Boolean(f.one_of_a_kind)} onCheckedChange={(v) => setF({ ...f, one_of_a_kind: v })} />
            <span className="font-body text-sm text-ink">Say “one of a kind” on this piece</span>
          </div>
          <div className="flex justify-end gap-3 border-t border-hairline pt-5">
            <button type="button" onClick={onClose} className="px-5 py-3 font-body text-sm text-stone hover:text-ink">Cancel</button>
            <button type="submit" data-testid="product-save" disabled={busy || f.images.length === 0} className="bg-ink px-6 py-3 text-paper hover:bg-ink/85 disabled:opacity-40">
              <span className="overline">{busy ? "Saving…" : "Save item"}</span>
            </button>
          </div>
          {f.images.length === 0 && <p className="text-right font-body text-xs text-muted">Add at least one photo to save.</p>}
        </form>
      </DialogContent>
    </Dialog>
  );
}
